import { Component, Input, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastService } from "@core/components/toasts/toasts.service";
import { ExcusedAbsenceService } from "services/corporate/excusedAbsence.service";
import { PointTimePeriodService } from "services/corporate/pointTimePeriod.service";
import { EmployeeModel } from "services/models/employee.model";
import { TimeOccurrenceParametersModel } from "services/models/timeOccurrenceParameters.model";
import { ExcusedAbsenceModel } from "services/models/corporate/excusedAbsence.model";
import { FileContentModel } from "services/models/fileContent";
import { FormValidations } from "utils/form-validation";
import { isValidDateValue } from "utils/helps";
import moment from "moment-business-days";
import { ExcusedAbsenceQualificationType } from "services/enums/ExcusedAbsenceQualificationType.enum";

@Component({
  selector: "app-create-excused-absence",
  templateUrl: "./create-excused-absence.component.html",
  styleUrls: ["./create-excused-absence.component.scss"],
})
export class CreateExcusedAbsenceModalComponent implements OnInit {
  @Input() employee: EmployeeModel = new EmployeeModel();
  @Input() parameters: TimeOccurrenceParametersModel = new TimeOccurrenceParametersModel();
  @Input() existingAbsences: ExcusedAbsenceModel[] = [];

  form: UntypedFormGroup;
  isLoading = false;
  uploadingFile = false;
  hasPeriods = false;
  selectedFileName = "";
  fileKey = "";
  fileName = "";

  private readonly allowedExtensions = ["pdf", "png", "jpg", "jpeg"];

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private excusedAbsenceService: ExcusedAbsenceService,
    private pointTimePeriodService: PointTimePeriodService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  get minAbsenceDate(): string | null {
    const maxDays = this.getMaxDaysForRetroactiveAbsence();
    if (maxDays <= 0) {
      return null;
    }
    return moment().subtract(maxDays, "days").format("YYYY-MM-DD");
  }

  get maxAbsenceDate(): string {
    return moment().format("YYYY-MM-DD");
  }

  get retroactiveDaysHint(): string | null {
    const maxDays = this.getMaxDaysForRetroactiveAbsence();
    if (maxDays <= 0) {
      return null;
    }
    return `É permitido registrar ausências de até ${maxDays} dia(s) atrás.`;
  }

  initializeForm(): void {
    const validators = [Validators.required];

    const minDate = this.minAbsenceDate;
    if (minDate) {
      validators.push(FormValidations.minDateValidator(minDate));
    }

    this.form = this.formBuilder.group({
      dateBegin: ["", validators],
    });
  }

  onDateBeginChange(): void {
    this.hasPeriods = false;
    this.clearUploadedFile();

    const selectedDate = this.form.controls["dateBegin"].value;
    if (!selectedDate || !isValidDateValue(selectedDate)) {
      return;
    }

    this.pointTimePeriodService
      .checkPeriodAvailableByCompany(this.employee.companyId, selectedDate)
      .subscribe((hasPeriods) => {
        this.hasPeriods = hasPeriods;
        if (!hasPeriods) {
          this.toast.showWarning("Não há Períodos em aberto na data selecionada.");
          this.form.controls["dateBegin"].setValue("");
        }
      });
  }

  handleFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    if (!this.form.controls["dateBegin"].valid) {
      this.toast.showWarning("Informe a data do afastamento antes de anexar o documento.");
      input.value = "";
      return;
    }

    if (!this.hasPeriods) {
      this.toast.showWarning("Não há Períodos em aberto na data selecionada.");
      input.value = "";
      return;
    }

    if (this.hasDuplicateForDate(this.form.controls["dateBegin"].value)) {
      this.toast.showWarning(this.buildDuplicateDateMessage(this.form.controls["dateBegin"].value));
      input.value = "";
      return;
    }

    const file = input.files[0];
    const extension = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();

    if (!this.allowedExtensions.includes(extension)) {
      this.toast.showWarning('Permitido apenas arquivos nos formatos: "PDF", "PNG", "JPG", "JPEG".');
      input.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const encodedBase64 = reader.result as string;
      const fileContent: FileContentModel = {
        encodedBase64: encodedBase64.split("base64,")[1],
        fileName: file.name,
        extension,
      };
      this.uploadDocument(fileContent);
    };

    reader.readAsDataURL(file);
  }

  handleSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.hasPeriods) {
      this.toast.showWarning("Não há Períodos em aberto na data selecionada.");
      return;
    }

    if (!this.fileKey) {
      this.toast.showWarning("Anexe o documento da ausência legal.");
      return;
    }

    const selectedDate = this.form.controls["dateBegin"].value;
    if (this.hasDuplicateForDate(selectedDate)) {
      this.toast.showWarning(this.buildDuplicateDateMessage(selectedDate));
      return;
    }

    const model = new ExcusedAbsenceModel();
    model.employeeId = this.employee.id;
    model.dateBegin = this.form.controls["dateBegin"].value;
    model.fileKey = this.fileKey;
    model.fileName = this.fileName;
    model.qualificationType = ExcusedAbsenceQualificationType.LegalAbsence.id.toString();

    this.isLoading = true;
    this.excusedAbsenceService.registerByEmployee(model).subscribe(
      () => {
        this.toast.showSuccess("Ausência legal registrada com sucesso!");
        this.activeModal.close(true);
      },
      () => {
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  onDismiss(): void {
    this.activeModal.dismiss(false);
  }

  private uploadDocument(fileContent: FileContentModel): void {
    this.uploadingFile = true;
    this.excusedAbsenceService
      .uploadTempFile({
        employeeId: this.employee.id,
        dateBegin: this.form.controls["dateBegin"].value,
        fileContent,
      })
      .subscribe(
        (res) => {
          this.uploadingFile = false;
          this.fileKey = res.data.fileKey;
          this.fileName = res.data.fileName;
          this.selectedFileName = res.data.fileName;
        },
        () => {
          this.uploadingFile = false;
          this.clearUploadedFile();
        }
      );
  }

  private clearUploadedFile(): void {
    this.fileKey = "";
    this.fileName = "";
    this.selectedFileName = "";
  }

  private getMaxDaysForRetroactiveAbsence(): number {
    const raw = Number(this.parameters?.daysToSendDocumentBeforeOccurrence ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      return 0;
    }
    return Math.floor(raw);
  }

  private hasDuplicateForDate(date: string): boolean {
    if (!date) {
      return false;
    }

    return this.existingAbsences
      .filter((absence) => absence.status !== "REPROVED")
      .some((absence) => this.coversDate(absence, date));
  }

  private coversDate(absence: ExcusedAbsenceModel, date: string): boolean {
    const target = moment(date, "YYYY-MM-DD", true);
    if (!target.isValid()) {
      return false;
    }

    const begin = moment(absence.dateBegin);
    if (!begin.isValid()) {
      return false;
    }

    let end = begin.clone();
    if (absence.amountDays > 0) {
      end = begin.clone().add(absence.amountDays - 1, "days");
    } else if (absence.dateEnd && absence.dateEnd !== "0001-01-01") {
      const parsedEnd = moment(absence.dateEnd);
      end = parsedEnd.isValid() ? parsedEnd : begin;
    }

    return target.isBetween(begin, end, "day", "[]");
  }

  private buildDuplicateDateMessage(date: string): string {
    return `Já existe uma ausência legal registrada para o dia ${moment(date).format("DD/MM/YYYY")}.`;
  }
}
